/*! *********************************************************************
 *
 * Copyright 2019 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *************************************************************************
 */

import _get from 'lodash/get';
import _map from 'lodash/map';
import _size from 'lodash/size';
import _concat from 'lodash/concat';

import { fetchGraphQL, mutateGraphQL } from '../helpers/dataHelpers';
import auth from './auth-service';
import {
  QUERY_ASSETS,
  QUERY_ASSETS_MANY,
  QUERY_ASSET,
  CREATE_ASSET,
  UPDATE_ASSET,
  DELETE_ASSET,
} from '../graphql/assets';

import uploadFile from '../services/UploadFile';
import validateAsset from '../resources/assets/validation';

const assetsProvider = {
  getList: async (resource, params) => {
    return fetchGraphQL(QUERY_ASSETS, resource, params);
  },

  getTotalList: async (resource, params) => {
    return concatPaginatedResults(resource, params);
  },

  getOne: async (resource, params) => {
    return fetchGraphQL(QUERY_ASSET, resource, params);
  },

  getMany: async (resource, params) => {
    return fetchGraphQL(QUERY_ASSETS_MANY, resource, params);
  },

  getManyReference: (resource, params) => Promise,

  create: async (resource, params) => {
    const asset = await createGraphQl(params.data);
    return { data: asset };
  },

  update: async (resource, params) => {
    const asset = await updateGraphQl(params);
    return { data: asset };
  },

  updateMany: async (resource, params) => {
    const data = params[resource];

    const errors = _map(data, (s) => {
      const err = validateAsset(s);
      if (_size(err) > 0) return s.extraData.ref;
    }).filter(Boolean);

    if (_size(errors) > 0) {
      throw new Error(
        _size(data) > 1
          ? `validation error on : ${errors.join(', ')}`
          : `validation error`,
      );
    }

    const result = await Promise.all(
      _map(data, async (item) => {
        const result = await updateGraphQl({ id: item.id, data: item });
        return result;
      }),
    );
    return { data: result, total: result.length };
  },

  delete: async (resource, params) => {
    const { deleteAsset } = await deleteGraphQl(params.id);
    if (!deleteAsset) {
      throw new Error(
        'Could not delete asset, it was previously published and/or bought',
      );
    }
    return { data: params.previousData };
  },

  deleteMany: (resource, params) => Promise,
};

async function updateGraphQl({ id, data }) {
  const updateAsset = await mutateGraphQL(
    UPDATE_ASSET,
    {
      id,
      asset: {
        title: data.title,
        description: data.description,
        status: data.status,
        cost: data.cost,
        tags: data.tags,
        categories: data.categories,
        extradata: _map(data.extraData, (v, k) => ({
          key: k.replace('_#_', '.'),
          value: v && String(v),
        })),
      },
    },
    'updateAsset',
  );

  return updateAsset;
}

async function deleteGraphQl(id) {
  return await mutateGraphQL(
    DELETE_ASSET,
    {
      id,
    },
    'deleteAsset',
  );
}

async function createGraphQl(asset) {
  let createdAsset;
  let fileUploadResult;
  const rawFile = _get(asset, 'assetFile.rawFile', null);
  const assetType = _get(asset, 'type', null);

  try {
    const accessToken = await auth.getAccessToken();
    fileUploadResult = await uploadFile(rawFile, accessToken);
  } catch (error) {
    console.error({ error });
    throw new Error('An error occur during the upload of the SBSAR');
  }

  try {
    createdAsset = await mutateGraphQL(
      CREATE_ASSET,
      {
        fileId: _get(fileUploadResult, 'body.id'),
        type: assetType,
      },
      'createAsset',
    );
  } catch (error) {
    console.error({ error });
    throw new Error('An error occur during the creation of the asset');
  }

  return createdAsset;
}

async function concatPaginatedResults(resource, params) {
  let result;
  let assets = [];
  let page = 1;

  // get assets chunk until all the assets have been fetch
  while (true) {
    const paramsWithPagination = {
      ...params,
      pagination: { page, perPage: 100 },
    };

    result = await fetchGraphQL(QUERY_ASSETS, resource, paramsWithPagination);

    const resultAssetsChunk = _get(result, 'data', {});
    assets = _concat(assets, resultAssetsChunk);

    const hasMore = _get(result, 'hasMore', false);
    if (!hasMore) {
      break;
    }

    ++page;
  }

  // rebuild collection with new assets list
  result.data = assets;
  return result;
}

export default assetsProvider;
